import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  DocumentTextIcon,
  UserGroupIcon,
  ChartBarIcon,
  AdjustmentsVerticalIcon
} from '@heroicons/react/24/outline';
import { leaveAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Leaves = () => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  const [activeTab, setActiveTab] = useState('my-requests');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [newLeaveRequest, setNewLeaveRequest] = useState({
    leave_type_id: '',
    start_date: '',
    end_date: '',
    reason: '',
    is_emergency: false,
    attachment_path: ''
  });

  const [approvalAction, setApprovalAction] = useState({
    action: '', // 'approve' or 'reject'
    comments: '',
    rejection_reason: ''
  });

  const { user, isEmployee, isManager, isAdmin, isHRManager } = useAuth();

  const tabs = [
    { id: 'my-requests', name: 'My Requests', icon: CalendarIcon },
    { id: 'team-requests', name: 'Team Requests', icon: UserGroupIcon, roles: ['manager', 'admin', 'hr_manager'] },
    { id: 'all-requests', name: 'All Requests', icon: DocumentTextIcon, roles: ['admin', 'hr_manager'] },
    { id: 'balance', name: 'Leave Balance', icon: ChartBarIcon }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  useEffect(() => {
    loadData();
  }, [activeTab, statusFilter]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const promises = [fetchLeaveTypes()];
      
      if (activeTab === 'balance') {
        promises.push(fetchLeaveBalance());
      } else {
        promises.push(fetchLeaveRequests());
        if (activeTab === 'team-requests' || activeTab === 'all-requests') {
          promises.push(fetchEmployees());
        }
      }
      
      await Promise.all(promises);
    } catch (error) {
      console.error('Error loading leave data:', error);
      setError('Failed to load leave data');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveRequests = async () => {
    try {
      const params = {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        manager_view: activeTab === 'team-requests' ? 'true' : undefined
      };

      const response = await leaveAPI.getRequests(params);
      setLeaveRequests(response.data.leave_requests || []);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
      throw error;
    }
  };

  const fetchLeaveTypes = async () => {
    try {
      const response = await leaveAPI.getTypes();
      setLeaveTypes(response.data.leave_types || []);
    } catch (error) {
      console.error('Error fetching leave types:', error);
      throw error;
    }
  };

  const fetchLeaveBalance = async () => {
    try {
      const response = await leaveAPI.getBalance();
      setLeaveBalance(response.data.leave_balance || []);
    } catch (error) {
      console.error('Error fetching leave balance:', error);
      throw error;
    }
  };

  const fetchEmployees = async () => {
    try {
      // This would need to be implemented in the API
      // const response = await employeeAPI.getAll({});
      // setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleSubmitLeaveRequest = async (e) => {
    e.preventDefault();
    
    try {
      const response = await leaveAPI.create(newLeaveRequest);
      
      if (response.data.success) {
        setShowCreateModal(false);
        setNewLeaveRequest({
          leave_type_id: '',
          start_date: '',
          end_date: '',
          reason: '',
          is_emergency: false,
          attachment_path: ''
        });
        
        await fetchLeaveRequests();
        await fetchLeaveBalance();
        
        alert(response.data.message || 'Leave request submitted successfully');
      }
    } catch (error) {
      console.error('Error submitting leave request:', error);
      alert(error.response?.data?.message || 'Failed to submit leave request');
    }
  };

  const handleApprovalAction = async (e) => {
    e.preventDefault();
    
    if (!selectedRequest || !approvalAction.action) return;
    
    try {
      let response;
      
      if (approvalAction.action === 'approve') {
        response = await leaveAPI.approve(selectedRequest.id, {
          comments: approvalAction.comments
        });
      } else if (approvalAction.action === 'reject') {
        if (!approvalAction.rejection_reason.trim()) {
          alert('Rejection reason is required');
          return;
        }
        response = await leaveAPI.reject(selectedRequest.id, {
          rejection_reason: approvalAction.rejection_reason
        });
      }
      
      if (response?.data.success) {
        setShowApprovalModal(false);
        setSelectedRequest(null);
        setApprovalAction({ action: '', comments: '', rejection_reason: '' });
        await fetchLeaveRequests();
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Error processing leave approval:', error);
      alert(error.response?.data?.message || `Failed to ${approvalAction.action} leave request`);
    }
  };

  const handleCancelRequest = async (requestId, reason = '') => {
    if (!window.confirm('Are you sure you want to cancel this leave request?')) {
      return;
    }
    
    try {
      const response = await leaveAPI.cancel(requestId, { reason });
      
      if (response.data.success) {
        await fetchLeaveRequests();
        await fetchLeaveBalance();
        alert('Leave request cancelled successfully');
      }
    } catch (error) {
      console.error('Error cancelling leave request:', error);
      alert(error.response?.data?.message || 'Failed to cancel leave request');
    }
  };

  const openApprovalModal = (request, action) => {
    setSelectedRequest(request);
    setApprovalAction({ ...approvalAction, action });
    setShowApprovalModal(true);
  };

  const openViewModal = async (request) => {
    try {
      const response = await leaveAPI.getById(request.id);
      if (response.data.success) {
        setSelectedRequest({
          ...response.data.leave_request,
          audit_log: response.data.audit_log
        });
        setShowViewModal(true);
      }
    } catch (error) {
      console.error('Error fetching leave details:', error);
      alert('Failed to load leave request details');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: ClockIcon },
      approved: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircleIcon },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircleIcon },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', icon: XCircleIcon },
      withdrawn: { bg: 'bg-gray-100', text: 'text-gray-800', icon: XCircleIcon },
      expired: { bg: 'bg-gray-100', text: 'text-gray-800', icon: ExclamationTriangleIcon }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const IconComponent = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <IconComponent className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const canApproveReject = (request) => {
    if (!request || request.status !== 'pending') return false;
    
    // Admin and HR managers can approve any request
    if (isAdmin() || isHRManager()) return true;
    
    // Managers can approve requests for their team members
    if (isManager() && request.manager_id === user.employee_id) return true;
    
    return false;
  };

  const renderMyRequests = () => (
    <div className="space-y-4">
      {leaveRequests.length === 0 ? (
        <div className="text-center py-12">
          <CalendarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No leave requests</h3>
          <p className="text-gray-600 mb-4">You haven't submitted any leave requests yet.</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Request Leave
          </button>
        </div>
      ) : (
        leaveRequests.map((request) => (
          <div key={request.id} className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-medium text-gray-900">
                  {request.leave_type_name}
                </h3>
                {getStatusBadge(request.status)}
                {request.is_emergency && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                    Emergency
                  </span>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => openViewModal(request)}
                  className="btn-secondary text-sm"
                >
                  <EyeIcon className="w-4 h-4 mr-1" />
                  View
                </button>
                {request.status === 'pending' && (
                  <button
                    onClick={() => handleCancelRequest(request.id)}
                    className="btn-secondary text-sm text-red-600 hover:text-red-700"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">From:</span>
                <p className="text-gray-900">{formatDate(request.start_date)}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">To:</span>
                <p className="text-gray-900">{formatDate(request.end_date)}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Days:</span>
                <p className="text-gray-900">{request.days_requested}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Submitted:</span>
                <p className="text-gray-900">{formatDate(request.submitted_at || request.created_at)}</p>
              </div>
            </div>
            
            {request.reason && (
              <div className="mt-4">
                <span className="font-medium text-gray-700">Reason:</span>
                <p className="text-gray-900 mt-1">{request.reason}</p>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  const renderTeamRequests = () => (
    <div className="space-y-4">
      {leaveRequests.length === 0 ? (
        <div className="text-center py-12">
          <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No team requests</h3>
          <p className="text-gray-600">No leave requests require your approval.</p>
        </div>
      ) : (
        leaveRequests.map((request) => (
          <div key={request.id} className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {request.first_name} {request.last_name}
                  </h3>
                  <p className="text-sm text-gray-600">{request.employee_number}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">{request.leave_type_name}</span>
                  {getStatusBadge(request.status)}
                </div>
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={() => openViewModal(request)}
                  className="btn-secondary text-sm"
                >
                  <EyeIcon className="w-4 h-4 mr-1" />
                  View
                </button>
                {canApproveReject(request) && (
                  <>
                    <button
                      onClick={() => openApprovalModal(request, 'approve')}
                      className="btn-primary text-sm"
                    >
                      <CheckCircleIcon className="w-4 h-4 mr-1" />
                      Approve
                    </button>
                    <button
                      onClick={() => openApprovalModal(request, 'reject')}
                      className="btn-secondary text-sm text-red-600 hover:text-red-700"
                    >
                      <XCircleIcon className="w-4 h-4 mr-1" />
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">From:</span>
                <p className="text-gray-900">{formatDate(request.start_date)}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">To:</span>
                <p className="text-gray-900">{formatDate(request.end_date)}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Days:</span>
                <p className="text-gray-900">{request.days_requested}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Submitted:</span>
                <p className="text-gray-900">{formatDate(request.submitted_at || request.created_at)}</p>
              </div>
            </div>
            
            {request.reason && (
              <div className="mt-4">
                <span className="font-medium text-gray-700">Reason:</span>
                <p className="text-gray-900 mt-1">{request.reason}</p>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  const renderLeaveBalance = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {leaveBalance.map((balance) => (
        <div key={balance.leave_type} className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{balance.leave_type}</h3>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Allowed:</span>
              <span className="font-medium text-gray-900">{balance.days_allowed} days</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Used:</span>
              <span className="font-medium text-gray-900">{balance.days_used} days</span>
            </div>
            
            <div className="flex justify-between border-t pt-2">
              <span className="text-gray-600">Remaining:</span>
              <span className={`font-medium ${balance.days_remaining > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {balance.days_remaining} days
              </span>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-4">
            <div className="bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${balance.days_remaining > 0 ? 'bg-green-500' : 'bg-red-500'}`}
                style={{ width: `${Math.min(100, (balance.days_used / balance.days_allowed) * 100)}%` }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'my-requests':
        return renderMyRequests();
      case 'team-requests':
        return renderTeamRequests();
      case 'all-requests':
        return renderTeamRequests(); // Similar to team requests but broader scope
      case 'balance':
        return renderLeaveBalance();
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leave Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage leave requests, approvals, and balances
          </p>
        </div>
        
        {activeTab !== 'balance' && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Request Leave
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            // Check role permissions for tab
            if (tab.roles && !tab.roles.some(role => user.role === role)) {
              return null;
            }
            
            const isActive = activeTab === tab.id;
            const IconComponent = tab.icon;
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                  ${isActive
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <IconComponent className="w-5 h-5 mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Status Filter (for request tabs) */}
      {activeTab !== 'balance' && (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <label htmlFor="status-filter" className="text-sm font-medium text-gray-700">
              Filter by Status:
            </label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-select"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {renderTabContent()}

      {/* Create Leave Request Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full m-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Request Leave</h3>
            </div>
            
            <form onSubmit={handleSubmitLeaveRequest} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Leave Type
                </label>
                <select
                  value={newLeaveRequest.leave_type_id}
                  onChange={(e) => setNewLeaveRequest({ ...newLeaveRequest, leave_type_id: e.target.value })}
                  className="form-select"
                  required
                >
                  <option value="">Select leave type</option>
                  {leaveTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={newLeaveRequest.start_date}
                    onChange={(e) => setNewLeaveRequest({ ...newLeaveRequest, start_date: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={newLeaveRequest.end_date}
                    onChange={(e) => setNewLeaveRequest({ ...newLeaveRequest, end_date: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason
                </label>
                <textarea
                  value={newLeaveRequest.reason}
                  onChange={(e) => setNewLeaveRequest({ ...newLeaveRequest, reason: e.target.value })}
                  className="form-input"
                  rows="3"
                  placeholder="Please provide a reason for your leave request"
                  required
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_emergency"
                  checked={newLeaveRequest.is_emergency}
                  onChange={(e) => setNewLeaveRequest({ ...newLeaveRequest, is_emergency: e.target.checked })}
                  className="form-checkbox"
                />
                <label htmlFor="is_emergency" className="ml-2 text-sm text-gray-700">
                  This is an emergency leave request
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full m-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {approvalAction.action === 'approve' ? 'Approve' : 'Reject'} Leave Request
              </h3>
            </div>
            
            <form onSubmit={handleApprovalAction} className="px-6 py-4 space-y-4">
              <div className="text-sm text-gray-600 space-y-2">
                <p><strong>Employee:</strong> {selectedRequest.first_name} {selectedRequest.last_name}</p>
                <p><strong>Leave Type:</strong> {selectedRequest.leave_type_name}</p>
                <p><strong>Period:</strong> {formatDate(selectedRequest.start_date)} to {formatDate(selectedRequest.end_date)}</p>
                <p><strong>Days:</strong> {selectedRequest.days_requested}</p>
                {selectedRequest.reason && <p><strong>Reason:</strong> {selectedRequest.reason}</p>}
              </div>
              
              {approvalAction.action === 'approve' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Comments (Optional)
                  </label>
                  <textarea
                    value={approvalAction.comments}
                    onChange={(e) => setApprovalAction({ ...approvalAction, comments: e.target.value })}
                    className="form-input"
                    rows="3"
                    placeholder="Add any comments..."
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rejection Reason *
                  </label>
                  <textarea
                    value={approvalAction.rejection_reason}
                    onChange={(e) => setApprovalAction({ ...approvalAction, rejection_reason: e.target.value })}
                    className="form-input"
                    rows="3"
                    placeholder="Please provide a reason for rejection..."
                    required
                  />
                </div>
              )}
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowApprovalModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className={approvalAction.action === 'approve' ? 'btn-primary' : 'btn-secondary text-red-600 hover:text-red-700'}
                >
                  {approvalAction.action === 'approve' ? 'Approve' : 'Reject'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Request Modal */}
      {showViewModal && selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Leave Request Details</h3>
            </div>
            
            <div className="px-6 py-4 space-y-6">
              {/* Request Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Employee</label>
                  <p className="text-gray-900">{selectedRequest.first_name} {selectedRequest.last_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Employee Number</label>
                  <p className="text-gray-900">{selectedRequest.employee_number}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Leave Type</label>
                  <p className="text-gray-900">{selectedRequest.leave_type_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  {getStatusBadge(selectedRequest.status)}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date</label>
                  <p className="text-gray-900">{formatDate(selectedRequest.start_date)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date</label>
                  <p className="text-gray-900">{formatDate(selectedRequest.end_date)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Days Requested</label>
                  <p className="text-gray-900">{selectedRequest.days_requested}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Submitted</label>
                  <p className="text-gray-900">{formatDate(selectedRequest.submitted_at || selectedRequest.created_at)}</p>
                </div>
              </div>
              
              {selectedRequest.reason && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Reason</label>
                  <p className="text-gray-900 mt-1">{selectedRequest.reason}</p>
                </div>
              )}
              
              {selectedRequest.manager_first_name && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Manager</label>
                  <p className="text-gray-900">{selectedRequest.manager_first_name} {selectedRequest.manager_last_name}</p>
                </div>
              )}
              
              {(selectedRequest.hr_comments || selectedRequest.rejection_reason) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Comments</label>
                  <p className="text-gray-900 mt-1">{selectedRequest.hr_comments || selectedRequest.rejection_reason}</p>
                </div>
              )}
              
              {/* Audit Log */}
              {selectedRequest.audit_log && selectedRequest.audit_log.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Activity Log</label>
                  <div className="space-y-2">
                    {selectedRequest.audit_log.map((log, index) => (
                      <div key={index} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                        <div>
                          <span className="font-medium">{log.action}</span>
                          {log.first_name && <span className="text-gray-600"> by {log.first_name} {log.last_name}</span>}
                          {log.comments && <span className="text-gray-600"> - {log.comments}</span>}
                        </div>
                        <span className="text-gray-500">{formatDate(log.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowViewModal(false)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaves;